import { defineStore } from 'pinia'

import { scorecardApi } from '../api/ScorecardApi'
import type { Lens, Lifecycle } from '../models/scorecard.types'
import type {
  BoardSummary,
  BoardTotalEntry,
  BreakdownSummaryEntry,
  BreakdownValuesPayload,
  ColumnEditBody,
  CreateSetBody,
  CreateSetPayload,
  CsvConfig,
  CsvInspectPayload,
  CsvResultPayload,
  RollupPayload,
  ScorecardPivotPayload,
  TargetRow,
  UpdateSetBody,
  UpdateSetPayload,
} from '../models/scorecardApi.types'
import { DEFAULT_LENS, DEFAULT_LIFECYCLE } from '../scorecardConfig'
import { reportScorecardFetchFailure } from '../telemetry/scorecardTelemetry'

import { useAccount, useWorkspace } from '@/features/account'
import { ApiError } from '@/shared/http'

export interface ScorecardState {
  targets: TargetRow[]
  breakdownSummary: BreakdownSummaryEntry[]
  boardTotals: Record<string, Record<string, BoardTotalEntry>>
  loading: boolean
  loaded: boolean
  error: boolean
  /** Active lens (Pace ⇄ Forecast) — shared across List and Grid; no refetch on toggle. */
  lens: Lens
  /** Active period filter (Current ⇄ Completed) — client-side, no refetch on toggle. */
  lifecycle: Lifecycle
  /** Workspace the loaded targets belong to — refetch when the workspace changes. */
  loadedWorkspaceId: number | string | null
  /** Account is part of the data scope even when two accounts reuse the same workspace id. */
  loadedAccountId: number | string | null
  /** Latest request wins across account/workspace switches and forced refreshes. */
  fetchGeneration: number
}

export const useScorecardStore = defineStore('scorecardStore', {
  state: (): ScorecardState => ({
    targets: [],
    breakdownSummary: [],
    boardTotals: {},
    loading: false,
    loaded: false,
    error: false,
    lens: DEFAULT_LENS,
    lifecycle: DEFAULT_LIFECYCLE,
    loadedWorkspaceId: null,
    loadedAccountId: null,
    fetchGeneration: 0,
  }),

  getters: {
    hasTargets: (s): boolean => s.targets.length > 0,
    isEmpty: (s): boolean => s.loaded && s.targets.length === 0,
    /** True once data is loaded but nothing exists in the active lifecycle bucket. */
    isLifecycleEmpty(): boolean {
      return this.loaded && this.targets.length > 0 && this.visibleTargets.length === 0
    },
    /**
     * Targets in the active lifecycle bucket. `completed` = backend `status` of
     * 'completed'; `current` = everything else (pending / current / empty).
     */
    visibleTargets: (s): TargetRow[] => {
      const wantCompleted = s.lifecycle === 'completed'
      return s.targets.filter((t) => (t.status === 'completed') === wantCompleted)
    },
  },

  actions: {
    /** Resolve the account/workspace pair once for every board-scoped read or write. */
    scorecardContext(): { accountId: number; workspaceId: number } | null {
      const accountId = useAccount().accountId.value
      const workspaceId = useWorkspace().workspaceId.value
      if (!accountId || !workspaceId) return null
      return { accountId: Number(accountId), workspaceId: Number(workspaceId) }
    },

    setLens(lens: Lens): void {
      this.lens = lens
    },

    setLifecycle(lifecycle: Lifecycle): void {
      this.lifecycle = lifecycle
    },

    /** Fetch the target list for the current account/workspace (module 07 accessors). */
    async fetchTargets(force = false, signal?: AbortSignal): Promise<void> {
      const accountId = useAccount().accountId.value
      const workspaceId = useWorkspace().workspaceId.value
      if (!accountId || !workspaceId) {
        this.reset()
        return
      }
      // Refetch when the workspace switched, even if previously loaded.
      const stale = this.loadedAccountId !== accountId || this.loadedWorkspaceId !== workspaceId
      if (this.loading && !force) return
      if (this.loaded && !force && !stale) return

      const request = ++this.fetchGeneration
      if (stale) {
        this.targets = []
        this.breakdownSummary = []
        this.boardTotals = {}
        this.loaded = false
        this.loadedAccountId = null
        this.loadedWorkspaceId = null
      }
      this.loading = true
      this.error = false
      try {
        const data = await scorecardApi.listTargets(Number(accountId), Number(workspaceId), signal)
        if (
          request !== this.fetchGeneration ||
          useAccount().accountId.value !== accountId ||
          useWorkspace().workspaceId.value !== workspaceId
        ) {
          return
        }
        this.targets = data.targets ?? []
        this.breakdownSummary = data.breakdown_summary ?? []
        this.boardTotals = data.totals ?? {}
        this.loaded = true
        this.loadedAccountId = accountId
        this.loadedWorkspaceId = workspaceId
      } catch (e) {
        if (request !== this.fetchGeneration) return
        if (ApiError.isApiError(e) && e.kind === 'canceled') throw e
        reportScorecardFetchFailure(
          ApiError.isApiError(e)
            ? { kind: e.kind, status: e.status, code: e.code }
            : { kind: 'unknown', status: null, code: null },
        )
        this.error = true
      } finally {
        if (request === this.fetchGeneration) this.loading = false
      }
    },

    refresh(signal?: AbortSignal): Promise<void> {
      return this.fetchTargets(true, signal)
    },

    /** Board reads stay behind the store so view components never import the HTTP client. */
    async readBoards(signal?: AbortSignal): Promise<BoardSummary[]> {
      const context = this.scorecardContext()
      if (!context) return []
      const data = await scorecardApi.listBoards(context.accountId, context.workspaceId, signal)
      return data.boards ?? []
    },

    async readBoard(boardId: number, signal?: AbortSignal): Promise<ScorecardPivotPayload | null> {
      const context = this.scorecardContext()
      if (!context) return null
      return scorecardApi.getBoard(context.accountId, context.workspaceId, boardId, signal)
    },

    async readRollup(
      boardId: number,
      groupBy: string,
      signal?: AbortSignal,
    ): Promise<RollupPayload | null> {
      const context = this.scorecardContext()
      if (!context) return null
      return scorecardApi.getRollup(
        context.accountId,
        context.workspaceId,
        boardId,
        groupBy,
        signal,
      )
    },

    async readBreakdownValues(
      breakdownType: string,
      dataLevel?: string | null,
      subBreakdownType?: string | null,
      signal?: AbortSignal,
    ): Promise<BreakdownValuesPayload | null> {
      const context = this.scorecardContext()
      if (!context) return null
      return scorecardApi.getBreakdownValues(
        context.accountId,
        context.workspaceId,
        breakdownType,
        dataLevel,
        subBreakdownType,
        signal,
      )
    },

    async createScorecard(body: CreateSetBody): Promise<CreateSetPayload | null> {
      const context = this.scorecardContext()
      if (!context) return null
      const result = await scorecardApi.createSet(context.accountId, context.workspaceId, body)
      await this.refresh()
      return result
    },

    async editScorecard(boardId: number, body: UpdateSetBody): Promise<UpdateSetPayload | null> {
      const context = this.scorecardContext()
      if (!context) return null
      const result = await scorecardApi.updateBoard(
        context.accountId,
        context.workspaceId,
        boardId,
        body,
      )
      await this.refresh()
      return result
    },

    async csvInspect(
      file: File,
      opts?: { delimiter?: string; encoding?: string },
      signal?: AbortSignal,
    ): Promise<CsvInspectPayload | null> {
      const context = this.scorecardContext()
      if (!context) return null
      return scorecardApi.csvInspect(context.accountId, context.workspaceId, file, opts, signal)
    },

    async csvPreview(
      file: File,
      config: CsvConfig,
      signal?: AbortSignal,
    ): Promise<CsvResultPayload | null> {
      const context = this.scorecardContext()
      if (!context) return null
      return scorecardApi.csvPreview(context.accountId, context.workspaceId, file, config, signal)
    },

    /**
     * Board writes refresh the canonical target list before resolving. The view then refreshes
     * its pivot/roll-up read, so the two representations cannot drift after a mutation.
     */
    async updateBoardColumn(boardId: number, metric: string, body: ColumnEditBody): Promise<void> {
      const context = this.scorecardContext()
      if (!context) return
      await scorecardApi.updateColumn(context.accountId, context.workspaceId, boardId, metric, body)
      await this.refresh()
    },

    async removeBoardColumn(boardId: number, metric: string): Promise<void> {
      const context = this.scorecardContext()
      if (!context) return
      await scorecardApi.deleteColumn(context.accountId, context.workspaceId, boardId, metric)
      await this.refresh()
    },

    async removeBoard(boardId: number): Promise<void> {
      const context = this.scorecardContext()
      if (!context) return
      await scorecardApi.deleteBoard(context.accountId, context.workspaceId, boardId)
      await this.refresh()
    },

    /**
     * Retarget one cell (PATCH). On success patches the row in place from the
     * server's echoed `target_value`. Re-throws so the caller can surface
     * INVALID_TARGET_VALUE inline.
     */
    async updateTargetValue(targetId: number, value: string): Promise<void> {
      const accountId = useAccount().accountId.value
      const workspaceId = useWorkspace().workspaceId.value
      if (!accountId || !workspaceId) return
      const data = await scorecardApi.updateTarget(
        Number(accountId),
        Number(workspaceId),
        targetId,
        value,
      )
      const next = data.target_value
      const row = this.targets.find((t) => t.id === targetId)
      if (row && next != null) row.target_value = next
    },

    /** Delete one cell (DELETE). On success removes it from state. Re-throws on failure. */
    async removeTarget(targetId: number): Promise<void> {
      const accountId = useAccount().accountId.value
      const workspaceId = useWorkspace().workspaceId.value
      if (!accountId || !workspaceId) return
      await scorecardApi.deleteTarget(Number(accountId), Number(workspaceId), targetId)
      this.targets = this.targets.filter((t) => t.id !== targetId)
    },

    /** Clears the server data + flags, not the toggles (contract 10 §7 reset semantics). */
    reset(): void {
      this.fetchGeneration += 1
      this.targets = []
      this.breakdownSummary = []
      this.boardTotals = {}
      this.loading = false
      this.loaded = false
      this.error = false
      this.loadedWorkspaceId = null
      this.loadedAccountId = null
    },
  },
})
